/**
 * Client-side scoring mirror of backend VenueRiskAssessmentScorer.js
 */

const LEGACY_DIMENSION_MAP = {
  operational_controls: 'operational',
  customer_patron: 'customer',
  geographic_environmental: 'geographic',
  mltf_indicators: 'mlTf',
}

function bandForScore(overall, bands = {}) {
  const low = Number(bands.low_max ?? bands.lowMax ?? 30)
  const med = Number(bands.medium_max ?? bands.mediumMax ?? 60)
  if (overall <= low) return 'low'
  if (overall <= med) return 'medium'
  return 'high'
}

function scoreQuestion(question, value) {
  if (question.scored === false || question.type === 'text' || question.type === 'number') {
    return { raw: 0, max: 0, scorePct: null }
  }
  if (question.type === 'select' && Array.isArray(question.options)) {
    const max = Math.max(...question.options.map((o) => Number(o.points) || 0), 0)
    const opt = question.options.find((o) => o.value === value)
    const raw = opt ? Number(opt.points) || 0 : 0
    const scorePct = max > 0 ? Math.round((raw / max) * 100) : 0
    return { raw, max, scorePct }
  }
  if (question.type === 'boolean') {
    const t = Number(question.points_if_true ?? question.pointsIfTrue ?? 0)
    const f = Number(question.points_if_false ?? question.pointsIfFalse ?? 0)
    const max = Math.max(t, f)
    const truthy = value === true || value === 'true' || value === 1 || value === '1'
    const raw = truthy ? t : f
    const scorePct = max > 0 ? Math.round((raw / max) * 100) : 0
    return { raw, max, scorePct }
  }
  if (question.type === 'number') return { raw: 0, max: 0, scorePct: null }
  return { raw: 0, max: 0, scorePct: null }
}

function scoreDimension(dim, responses) {
  let raw = 0
  let max = 0
  let weightSum = 0
  let weightedTotal = 0
  let usesQuestionWeights = false

  for (const q of dim.questions || []) {
    const val = responses[q.id]
    const part = scoreQuestion(q, val)
    if (part.max <= 0 || part.scorePct == null) continue
    const qw = Number(q.weight)
    if (Number.isFinite(qw) && qw > 0) {
      usesQuestionWeights = true
      weightSum += qw
      weightedTotal += part.scorePct * qw
    }
    raw += part.raw
    max += part.max
  }

  if (usesQuestionWeights && weightSum > 0) {
    return Math.round(weightedTotal / weightSum)
  }
  return max > 0 ? Math.round((raw / max) * 100) : 0
}

export function scoreTemplate(definition, responses = {}) {
  if (!definition || !Array.isArray(definition.dimensions)) {
    return { dimensions: {}, legacy: {}, overall: 0, band: 'low' }
  }
  const dims = {}
  const legacy = {}
  let weightSum = 0
  let weightedTotal = 0

  for (const dim of definition.dimensions) {
    const dimScore = scoreDimension(dim, responses)
    dims[dim.id] = dimScore
    const legacyKey = LEGACY_DIMENSION_MAP[dim.id]
    if (legacyKey) legacy[legacyKey] = dimScore
    const w = Number(dim.weight) || 0
    weightSum += w
    weightedTotal += dimScore * w
  }

  const overall = weightSum > 0 ? Math.round(weightedTotal / weightSum) : 0
  return {
    dimensions: dims,
    legacy,
    overall,
    band: bandForScore(overall, definition.bands),
  }
}

export function legacyDetailsToResponses(details) {
  if (!details || typeof details !== 'object') return {}
  const out = { ...details }
  if (details.indicators && typeof details.indicators === 'object') {
    for (const [k, v] of Object.entries(details.indicators)) {
      out[k] = v
    }
    delete out.indicators
  }
  delete out.reason
  return out
}

export function emptyResponsesForTemplate(definition) {
  const out = {}
  if (!definition?.dimensions) return out
  for (const dim of definition.dimensions) {
    for (const q of dim.questions || []) {
      if (q.type === 'boolean') out[q.id] = false
      else out[q.id] = ''
    }
  }
  return out
}
